'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { Component, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Raven from 'raven-js';
import { NavMarker } from 'react-rhumb';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIApplication from 'UIComponents/app/UIApplication';
import AdBlockDetectedPrompt from 'AdsUI/components/AdBlockDetectedPrompt';
import LeadCostsActions from 'AdsUI/actions/LeadCostsActions';
import { fetchOverviewWithEarlyRequester, fetchMyPermissions, fetchAdProfile, fetchOverview } from 'AdsUI/actions/AdAccountsActions';
import ConnectedUsersActions from 'AdsUI/actions/ConnectedUsersActions';
import { hasScope, hasReadScope } from 'AdsUI/lib/Scopes';
import NotificationList from 'ReduxMessenger/components/NotificationList';
import { getAccountsWithUnacceptedToS } from 'AdsUI/actions/AudiencesActions';
import { addHeaderBanner } from 'AdsUI/actions/BannerActions';
import { changeFilters, initialize } from 'AdsUI/actions/GlobalFilterActions';
import BannerTypes from 'AdsUI/lib/BannerTypes';
import { AudienceSources, Networks } from 'AdsUI/lib/Constants';
import { getAdAccounts } from 'AdsUI/selectors/AccountsSelectors';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import PortalIdParser from 'PortalIdParser';
import NoAdsAccess from 'AdsUI/components/NoAdsAccess';
import GenericErrorBoundary from 'AdsUI/components/GenericErrorBoundary';
import ErrorMessageWithNavigation from 'AdsUI/components/ErrorMessageWithNavigation';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getAdProfile } from 'AdsUI/selectors/AdProfileSelectors';
import SharedDragDropContext from 'customer-data-ui-utilities/dnd/SharedDragDropContext';
import { fetchSelectedLeadSyncingEntities } from '../actions/LeadSyncingActions';
import { getSelectedLeadSyncingEntities } from '../selectors/LeadSyncingSelectors';
import { hasWriteScope } from 'AdsUI/selectors/AuthSelectors';
import GlobalAnnouncements from 'AdsUI/containers/announcements/GlobalAnnouncements';
import Survicate from './Survicate';
const LEAD_SYNC_ALERT_CUTOFF = 2 * 7 * 24 * 60 * 60 * 1000; // 2 weeks
// Pulled this out so we're not causing re-renders of application
//
// Note: lead ad creation relies on selected lead syncing entities having been fetched here.
// If this is removed, then make sure to add a fetch to AdCreationCreativeContainer or somewhere
// else appropriate.

const ShouldShowLeadSyncError = connect(state => ({
  fetchedSelectedLeadSyncingEntities: state.requestStatus.leadSyncSelected.fetched,
  selectedLeadSyncingEntities: getSelectedLeadSyncingEntities(state)
}))(({
  dispatch,
  fetchedSelectedLeadSyncingEntities,
  selectedLeadSyncingEntities
}) => {
  useEffect(() => {
    if (!fetchedSelectedLeadSyncingEntities) {
      dispatch(fetchSelectedLeadSyncingEntities());
    }
  }, [fetchedSelectedLeadSyncingEntities, dispatch]);
  useEffect(() => {
    const now = Date.now();
    const recentMissingEmail = selectedLeadSyncingEntities.some(entity => !!entity.lastSubmissionWithoutEmail && now - LEAD_SYNC_ALERT_CUTOFF < entity.lastSubmissionWithoutEmail);

    if (recentMissingEmail) {
      dispatch(addHeaderBanner(BannerTypes.LEAD_SYNC_MISSING_EMAIL));
    }
  }, [selectedLeadSyncingEntities, dispatch]);
  return null;
});

class Application extends Component {
  constructor(props) {
    super(props);
    this.state = {
      adBlockDetected: false
    };
  }

  componentDidMount() {
    const {
      location,
      history,
      hasAdsWrite
    } = this.props;

    if (document.getElementById('ylGOtreHWo1ad2jS') === null) {
      // eslint-disable-next-line no-console
      console.log('Detected an adblocker');
      this.props.addHeaderBanner(BannerTypes.ADBLOCK_DETECTED);
      this.setState({
        adBlockDetected: true
      });
      Raven.setExtraContext({
        adBlockDetected: true
      });
      return;
    } // eslint-disable-next-line no-console


    console.log('No ad block detected');
    Raven.setExtraContext({
      adBlockDetected: false
    });

    if (!hasReadScope()) {
      // Don't make any fetch calls if the user doesn't have read permission.
      return;
    } // callback routes and onboarding wizard routes should always be displayed


    Promise.all([this.props.fetchSelectedAdAccountsOverview(), this.props.fetchConnectedUsers(), this.props.fetchAdProfile()]).then(values => {
      const [accountsOverview] = values;
      const anyAccountConnected = accountsOverview && accountsOverview.length;
      this.initializeAppTracking();
      this.props.initializeFilters();

      if (hasAdsWrite) {
        this.props.fetchMyPermissions();
      }

      if (!this.isOnboardingWizard()) {
        if (!anyAccountConnected) {
          // Redirect to onboarding if certain conditions met
          if (location.pathname !== '/onboarding') {
            history.replace(`onboarding${location.search}`);
          }

          return;
        }

        if (hasAdsWrite && this.shouldShowPermissionBanner()) {
          this.props.addHeaderBanner(BannerTypes.APP_PERMISSIONS_ALERT);
        }
      } // if you navigate to onboarding but you have anything connected, go to campaigns


      if (location.pathname === '/onboarding' && anyAccountConnected) {
        history.replace('/');
      } // Temporary call we need to make to determine whether user needs to re-accept the Facebook ToS
      // for custom audiences.


      if (!this.props.fetchedAccountsWithUnacceptedToS) {
        this.props.getAccountsWithUnacceptedToS(Networks.FACEBOOK, AudienceSources.HUBSPOT_CONTACT_LIST);
      }

      if (hasScope('ads-roi-reporting')) {
        this.props.fetchLeadCosts();
      }

      if (accountsOverview.length > 3) {
        return;
      } // fetch rest of user accounts


      this.props.fetchOverview();
    }).catch(error => {
      Raven.captureException(error);
    });
  }

  shouldShowPermissionBanner() {
    const {
      connectedUsers
    } = this.props;
    return !!connectedUsers.find(user => user.isMissingScopes());
  }

  isOnboardingWizard() {
    return this.props.location.pathname.indexOf('onboarding-wizard') >= 0;
  }

  isOnboarding() {
    return this.props.location.pathname === '/onboarding';
  }

  initializeAppTracking() {
    const {
      adProfile,
      user
    } = this.props;
    const {
      hasConversions
    } = adProfile;
    usageTracker.setProperties({
      hubId: PortalIdParser.get(),
      email: user.email,
      has_conversions: hasConversions
    });
  }

  renderBody() {
    const {
      adBlockDetected
    } = this.state;
    const {
      connectedUsersRequestStatus,
      adAccountsOverviewRequestStatus,
      adAccounts
    } = this.props;
    const fetchFailed = adAccountsOverviewRequestStatus.fetchFailed || connectedUsersRequestStatus.fetchFailed;
    const adAccountsFetched = adAccountsOverviewRequestStatus.fetched && connectedUsersRequestStatus.fetched;

    if (!hasReadScope()) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(NoAdsAccess, {}), /*#__PURE__*/_jsx(NavMarker, {
          name: "NO_ADS_ACCESS"
        })]
      });
    }

    if (adBlockDetected && (!adAccountsFetched || fetchFailed)) {
      // If adAccounts never are fetched, there's a good chance an adblocker is to blame
      return /*#__PURE__*/_jsx(AdBlockDetectedPrompt, {});
    }

    if (fetchFailed) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "CAMPAIGNS_PAGE_FAILURE",
        children: /*#__PURE__*/_jsx(UIErrorMessage, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "errors.genericError"
          })
        })
      });
    }

    if (adAccounts.length === 0 && !this.isOnboardingWizard() && !this.isOnboarding()) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(UILoadingSpinner, {
          grow: true,
          minHeight: 300,
          label: "..."
        }), /*#__PURE__*/_jsx(NavMarker, {
          name: "APP_LOADING"
        })]
      });
    }

    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(NotificationList, {}), /*#__PURE__*/_jsx(ShouldShowLeadSyncError, {}), /*#__PURE__*/_jsxs(GenericErrorBoundary, {
        fallback: /*#__PURE__*/_jsx(ErrorMessageWithNavigation, {}),
        children: [/*#__PURE__*/_jsx(GlobalAnnouncements, {}), this.props.children]
      }), /*#__PURE__*/_jsx(Survicate, {}), /*#__PURE__*/_jsx(NavMarker, {
        name: "APP_RENDERING"
      })]
    });
  }

  render() {
    return /*#__PURE__*/_jsx(UIApplication, {
      name: "ads",
      children: this.renderBody()
    });
  }

}

const select = state => {
  return {
    adProfile: getAdProfile(state),
    adAccounts: getAdAccounts(state),
    hasAdsWrite: hasWriteScope(state),
    connectedUsers: state.connectedUsers,
    user: state.auth.user,
    adAccountsOverviewRequestStatus: state.requestStatus.adAccountsOverview,
    adAccountsRequestStatus: state.requestStatus.adAccounts,
    connectedUsersRequestStatus: state.requestStatus.connectedUsers,
    fetchedAccountsWithUnacceptedToS: state.requestStatus.accountsWithUnacceptedToS.fetched
  };
};

const mapDispatchToProps = {
  addHeaderBanner,
  getAccountsWithUnacceptedToS,
  changeFilters,
  initializeFilters: initialize,
  fetchSelectedAdAccountsOverview: fetchOverviewWithEarlyRequester,
  fetchAdProfile,
  fetchMyPermissions,
  fetchOverview,
  fetchLeadCosts: LeadCostsActions.fetchLeadCosts,
  fetchConnectedUsers: ConnectedUsersActions.fetchWithEarlyRequester,
  refetchConnectedUsers: ConnectedUsersActions.fetch
};
export default connect(select, mapDispatchToProps)(withRouter(SharedDragDropContext()(Application)));